import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import isEqual from 'lodash/isEqual';
import { faTrash, faCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Button } from '@asu/components-core/dist/libCore.es';
import TabPanel from './TabPanel';

import CourseListComponent from './CourseListComponent';

import useSafeMutation from 'auth/useSafeMutation';
import { checksheetUpdate } from 'apis/checksheetAPIs';
import {
  isAuthError,
  cleanupSessionStorage,
  showRestorationAlert,
  parseSessionData,
} from 'auth/ServiceAuthUtils';
import HistoryDetails from './HistoryDetails';

// import { setCourses, setCourseListId } from 'state/slices/courseListSlice';

// import { classNames } from 'utils';
import { setCourseLists, setDetails } from 'state/slices/checksheetSlice';

const CourseListForm = ({
  courseListId,
  handleClose,
  isLocked = false,
  holdsLock = false,
}) => {
  const [tab, setTab] = useState(0); // or 'History'
  const [dataToModal, setDataToModal] = useState({});
  console.log('courseListIddd:', courseListId);

  const checksheetId = useSelector((state) => state.checksheet.checksheetId);
  // const courseListId = useSelector((state) => state.courseList.courseListId);
  // const courseListName = useSelector(
  //   (state) => state.courseList.courseListName
  // );
  const details = useSelector((state) => state.checksheet.details);
  const requirementList = useSelector(
    (state) => state.checksheet.checksheetRequirementList
  );
  // const courses = useSelector((state) => state.courseList.courses);

  const courseLists = useSelector((state) => state.checksheet.courseLists);

  const [courseListName, setCourseListName] = useState(
    courseListId !== null ? courseLists[courseListId]?.courseListName || '' : ''
  );
  const [courses, setCourses] = useState(
    courseListId !== null ? courseLists[courseListId]?.courses || [] : []
  );

  const historyRequirements = useSelector(
    (state) => state.history.requirements
  );

  console.log('checksheetId============', checksheetId);

  // const [showAsComponent, setShowAsComponent] = useState(true);
  // const [requirementType, setRequirementType] = useState(null);
  // const [newCourseListName, setNewCourseListName] = useState('');

  const hasHistory =
    historyRequirements != null && historyRequirements[courseListId] != null;

  const dispatch = useDispatch();

  // Use useSafeMutation for automatic data preservation on auth failure
  const { executeMutation: updateChecksheet } = useSafeMutation(
    checksheetUpdate,
    'courselist-form-data',
    { flagKey: 'courselist-modal-reopen', valueField: 'courseListId' }
  );

  // Create a ref to courseListComponent to call child function
  const courseListComponentRef = useRef(null);

  const saveLastRequirement = async () => {
    if (courseListComponentRef.current) {
      const updatedCourses =
        await courseListComponentRef.current.callChildFunction(
          'Hello from courselist form'
        );
      return updatedCourses;
    }
    return courses;
  };

  const handleDeleteCourseList = () => {
    const confirmDeletion = window.confirm(
      'Are you sure you want to delete this course list?'
    );
    if (confirmDeletion) {
      console.log('delete courseListId:', courseListId);
      saveCourseList(null, '');
      handleClose();
    }
  };

  const saveCourseList = async (newCourses, newCourseListName) => {
    if (!courseListName && !newCourseListName) {
      alert('A courseList name is required');
      return;
    }

    // useSafeMutation will handle data preservation on auth failure
    // No need for proactive save here

    try {
      const jsonData = details;
      console.log('jsonData:', jsonData);
      console.log('courseListId:', courseListId);
      let courseListUuid = courseListId || uuidv4();

      // Ensure courseLists is defined within jsonData
      let courseLists = jsonData.courseLists || {};

      // If courseLists is frozen, create a new object to prevent modification errors
      if (Object.isFrozen(courseLists)) {
        courseLists = { ...courseLists };
      }

      if (newCourses === null && courseLists[courseListId]) {
        // If newCourses is null, remove the course list
        delete courseLists[courseListId];
      } else {
        // Ensure courseListId is valid and exists within courseLists
        if (!courseLists[courseListUuid]) {
          // Initialize the course list if it doesn't exist
          courseLists[courseListUuid] = {
            courseListName: newCourseListName || courseListName,
            courses: courses || [],
          };
        }
        console.log('courseLists before update:', courseLists);

        const updatedCourses = new Set([...courses]);

        courseLists[courseListUuid] = {
          courseListName: newCourseListName || courseListName,
          courses: Array.from(newCourses || updatedCourses),
        };
      }

      console.log('courseLists after update:', courseLists);

      // Create updatedJsonData with the same structure as jsonData
      const updatedJsonData = {
        ...jsonData,
        componentRequirements: requirementList,
        courseLists: {
          // ...jsonData.courseLists,
          ...courseLists,
        },
      };

      // Call useSafeMutation's executeMutation with wrapped data including metadata
      const dataToSave = {
        courseListId: courseListUuid,
        courseListName: newCourseListName || courseListName,
        courses: newCourses ? Array.from(newCourses) : courses,
        checksheetData: updatedJsonData,
      };

      await updateChecksheet(
        {
          id: checksheetId,
          jsonData: updatedJsonData,
        },
        dataToSave // This metadata will be saved by useSafeMutation on auth failure
      );

      // Only update Redux state AFTER successful API save
      // This prevents UI from showing data as saved when backend save failed
      dispatch(setCourseLists(courseLists));
      console.log(
        '[CourseListForm] Successfully saved to backend, updating Redux state'
      );
      dispatch(setDetails(updatedJsonData));

      // Clear any stale reopen flag on successful save
      cleanupSessionStorage(
        ['courselist-modal-reopen', 'courselist-form-data'],
        'CourseListForm save success'
      );
    } catch (error) {
      console.error('Error saving course list:', error);

      // Check if this is an auth/session error by examining the error
      if (isAuthError(error)) {
        // useSafeMutation has already saved the data and set the reopen flag
        console.log(
          '[CourseListForm] Auth error detected - data already saved by useSafeMutation'
        );
      } else {
        // For non-auth errors, show user-friendly message
        alert('Failed to save course list. Please try again.');
      }
    }
  };

  // click Edit button to open modal and edit the data
  const openEditModal = (index, type, details) => {
    const modalData = {
      index: index,
      type: type,
      details: details,
    };

    setDataToModal(modalData);
    // modalSetAsRequirement(type);
  };

  //add index and req type to data. We need to know the index when data sent back from child component
  //and what type of the requirment it is.
  const createWrappedData = (index, type, details) => {
    const wrappedData = {
      index: index,
      type: type,
      details: details,
    };
    return wrappedData;
  };

  // Callback function to receive data from the modal
  const receiveDataFromModal = (data) => {
    console.log('Received data from modal:' + JSON.stringify(data));
    if (data) {
      if (
        !data.details ||
        !data.details.courses ||
        data.details.courses.length === 0
      ) {
        updateData(data.index, data.type, null);
      } else updateData(data.index, data.type, data.details);
    }
  };

  // Function to update parent data from a child
  const updateData = (index, type, newData) => {
    if (
      type === 'req_multiple' ||
      type === 'req_course' ||
      type === 'req_gs' ||
      type === 'req_text'
    ) {
      console.log('comonmentRequirementList+++++++', courses);

      if (index === 9999 && courses) {
        index = courses.length;
      } else if (index === 9999) {
        index = 0;
      }
      const newDataList = [...courses];
      if (!newData || !isEqual(newDataList[index], newData)) {
        if (!newData) {
          // if newData is empty (delete data)
          newDataList.splice(index, 1);
        } else {
          // if new data is not empty, then update data
          newDataList[index] = newData;
        }
      }

      console.log('new data list: ', newDataList);
      // dispatch(setCourses(newDataList));
      setCourses(newDataList);

      saveCourseList(newDataList, courseListName); // save course list data to database
    }
  };

  // when user click the save button, save the course list first and then submit the course list
  const handleSaveCourseList = async () => {
    if (!courseListName) {
      alert('A courseList name is required');
      return;
    }

    // Blur any focused input to ensure pending changes are committed
    if (document.activeElement && document.activeElement !== document.body) {
      document.activeElement.blur();
    }

    // Wait a tick for blur events to process
    await new Promise((resolve) => setTimeout(resolve, 0));

    // Get the updated courses from child component
    const updatedCourses = await saveLastRequirement();

    // Now save with the updated courses from the child
    saveCourseList(updatedCourses, courseListName);
    handleClose();
  };

  // Restore saved data on mount (from useSafeMutation)
  useEffect(() => {
    const savedData = sessionStorage.getItem('courselist-form-data');
    console.log('Checking for saved course list data:', savedData);
    if (savedData) {
      try {
        const parsedData = parseSessionData('courselist-form-data');
        console.log('Parsed saved data:', parsedData);

        // Restore the course list fields
        if (parsedData.courseListName) {
          setCourseListName(parsedData.courseListName);
        }
        if (parsedData.courses) {
          setCourses(parsedData.courses);
        }

        // Show restoration alert
        showRestorationAlert({
          formType: 'course list',
        });

        // Clean up
        cleanupSessionStorage(
          ['courselist-form-data'],
          'CourseListForm restoration'
        );
      } catch (error) {
        console.error('Error restoring saved data:', error);
      }
    }
  }, []); // Run once on mount

  // useEffect(() => {
  //   console.log('Setting courses...');
  //   setCourseItems(courses);
  // }, [courses]);

  return (
    <div style={{ width: '1224px' }}>
      <div className="px-6 pb-3 pt-6">
        <TabPanel
          // tabs={['Course List', 'History']}

          tabs={[
            'Course List',
            <>
              {hasHistory && (
                <FontAwesomeIcon
                  icon={faCircle}
                  className="text-teal underline-hover"
                  title="See changes"
                  style={{ width: '10px', paddingTop: '4px' }}
                />
              )}
              History
            </>,
          ]}
          currentTab={tab}
          setCurrentTab={setTab}
        >
          {/* Tab 0: Course List */}
          <>
            {isLocked && holdsLock ? (
              <div className="pt-4 mb-6">
                <div className="fs-4 fw-bold">Course list name</div>
                <input
                  type="text"
                  placeholder="Untitled course list"
                  className="fs-4 px-2 py-1 w-50"
                  name="courseListId"
                  value={courseListName}
                  onChange={(e) => setCourseListName(e.target.value)}
                />
              </div>
            ) : (
              <div className="fs-3 fw-bold mb-6">{courseListName}</div>
            )}

            <CourseListComponent
              ref={courseListComponentRef}
              id={checksheetId}
              year={details.year}
              courseListId={courseListId}
              edit={true}
              openEditModal={openEditModal}
              updateData={updateData}
              dataFromParent={dataToModal}
              dataToParent={(newData) => receiveDataFromModal(newData)}
              createWrappedData={createWrappedData}
              fromCourseList={true}
              courses={courses}
              setCourses={setCourses}
              handleClose={handleClose}
              isLocked={isLocked}
              holdsLock={holdsLock}
            />
          </>

          {/* Tab 1: History */}
          <div className="mt-4 px-2">
            <HistoryDetails
              historyCourseListRequirements={historyRequirements[courseListId]}
              handleClose={handleClose}
            />
          </div>
        </TabPanel>
      </div>

      {tab === 0 && (
        <div className="px-6 py-3 bg-gray-1">
          <div className="d-flex justify-content-between align-items-center">
            <div className="exclude-button d-flex gap-1 align-items-center">
              {isLocked && holdsLock && (
                <Button
                  label="Save"
                  color="maroon"
                  onClick={handleSaveCourseList}
                />
              )}
              <Button onClick={handleClose} label="Cancel" color="gray" />
            </div>
            {isLocked && holdsLock && (
              <div className="px-6 exclude-button">
                <span
                  className="button-link"
                  onClick={handleDeleteCourseList}
                  role="button"
                >
                  <FontAwesomeIcon icon={faTrash} className="me-1" />
                  Delete list
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseListForm;
